import React, { useEffect, useState } from 'react';
import { Grid, Card, CardContent, CardHeader, Box } from '@mui/material';
import { Theme, useTheme } from '@mui/material/styles';
import { 
  FinancialMetricsChart, 
  ComplianceStatus, 
  RecentTransactions, 
  UpcomingDeadlines 
} from './components';
import { ReportingEngine } from '../../services/ReportingEngine';
import { DashboardMetrics, TrendItem } from '../../types/reporting';
import styled from '@emotion/styled';

interface OverviewProps {
  fiscalSponsorId: string;
}

const StyledOverview = styled.div<{ theme: Theme }>`
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 24px;
  margin-top: 64px;
  min-height: calc(100vh - 64px);
  background: #121212;
  color: #ffffff;

  .MuiCard-root {
    background: #1a2035;
    color: #ffffff;
    border-radius: 8px;
  }

  .MuiCardHeader-root {
    .MuiCardHeader-title {
      color: #ffffff;
    }
  }
`;

export const DashboardOverview: React.FC<OverviewProps> = ({ fiscalSponsorId }) => {
  const [metrics, setMetrics] = useState<DashboardMetrics | null>(null);
  const [loading, setLoading] = useState(true);
  const theme = useTheme();
  const reportingEngine = new ReportingEngine();

  const loadOverviewData = async () => {
    try {
      const data = await reportingEngine.generateReport({
        fiscalSponsorId,
        startDate: new Date(new Date().setMonth(new Date().getMonth() - 12)),
        endDate: new Date()
      }, {
        type: 'dashboard'
      });
      
      // Transform the data to match DashboardMetrics type
      const dashboardData: DashboardMetrics = {
        trends: {
          revenue_trend: data.data.trends?.revenue_trend || [],
          expense_trend: data.data.trends?.expense_trend || []
        },
        total_fund_balance: data.data.total_fund_balance || 0,
        active_funds: data.data.active_funds || 0,
        average_fund_balance: data.data.average_fund_balance || 0,
        monthly_deposits: data.data.monthly_deposits || 0,
        monthly_withdrawals: data.data.monthly_withdrawals || 0,
        compliance_alerts: data.data.compliance_alerts || 0,
        upcoming_deadlines: data.data.upcoming_deadlines || [],
        recent_transactions: data.data.recent_transactions || []
      };
      
      setMetrics(dashboardData);
    } catch (error) {
      console.error('Failed to load overview data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadOverviewData();
  }, [fiscalSponsorId]);

  if (loading) {
    return <div>Loading...</div>;
  }

  const chartData = metrics?.trends ? [
    ...metrics.trends.revenue_trend.map((item: TrendItem) => ({ 
      date: new Date(item.date).toISOString(), 
      value: item.amount 
    })),
    ...metrics.trends.expense_trend.map((item: TrendItem) => ({ 
      date: new Date(item.date).toISOString(), 
      value: -item.amount 
    }))
  ] : [];

  const determinePriority = (type: string): 'low' | 'medium' | 'high' => {
    switch (type.toLowerCase()) {
      case 'urgent':
      case 'critical':
      case 'tax':
        return 'high';
      case 'report':
      case 'review':
        return 'medium';
      default:
        return 'low';
    }
  };

  return (
    <StyledOverview theme={theme}>
      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Card>
            <CardHeader title="Financial Overview" />
            <CardContent>
              <Box sx={{ mt: 4 }}>
                <FinancialMetricsChart
                  data={chartData}
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card>
            <CardHeader title="Compliance Status" />
            <CardContent>
              <ComplianceStatus 
                alerts={Number(metrics?.compliance_alerts) || 0}
                upcomingDeadlines={metrics?.upcoming_deadlines?.map(d => ({
                  ...d,
                  description: d.description,
                  date: new Date(d.date)
                })) || []}
              />
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardHeader title="Recent Transactions" />
            <CardContent>
              <RecentTransactions 
                transactions={metrics?.recent_transactions?.map(t => ({
                  ...t,
                  date: new Date(t.date)
                })) || []}
                onViewAll={() => {/* Navigate to transactions */}}
              />
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardHeader title="Upcoming Deadlines" />
            <CardContent>
              <UpcomingDeadlines 
                deadlines={metrics?.upcoming_deadlines?.map(d => ({
                  id: d.id,
                  title: d.description,
                  description: d.description,
                  date: new Date(d.date),
                  type: d.type,
                  priority: determinePriority(d.type)
                })) || []}
                onDeadlineClick={(deadline) => {/* Handle deadline click */}}
              />
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </StyledOverview>
  );
};

// For backward compatibility
export const Overview = DashboardOverview; 